import React, { useState, useEffect } from 'react';
import { useTheme, Tokens } from '../components/ThemeContext';
import { useResponsive } from '../components/useResponsive';
import { ccpSourceLabel, ccpSourceCategory, CCP_SOURCE_LABELS, bilingualSummary } from '../components/utils';

// ── Types ─────────────────────────────────────────────────────────────────────

interface ManufacturerProduct {
  id: number;
  ndc_code: string;
  product_name: string;
  generic_name?: string;
  dosage_form?: string;
  strength?: string;
  supply_chain_risk?: string;
  country_of_origin?: string;
  finished_goods_country?: string;
  shortage_status?: string;
}

interface SubsidiaryProductGroup {
  subsidiary_id: number;
  subsidiary_name: string;
  product_count: number;
  products: ManufacturerProduct[];
}

interface ManufacturerRecall {
  event_id: string;
  recall_number?: string;
  status?: string;
  classification?: string;
  reason_for_recall?: string;
  recalling_firm?: string;
  recall_initiation_date?: string;
  termination_date?: string;
  product_name?: string;
  ndc_code?: string;
}

interface ManufacturerShortage {
  id: number;
  shortage_status?: string;
  severity?: string;
  reason?: string;
  reported_date?: string;
  product_id?: number;
  product_name?: string;
  ndc_code?: string;
}

interface ManufacturerFacility {
  id: number;
  fei_number: string;
  firm_name: string;
  country?: string;
  country_iso3?: string;
  operations_raw?: string;
  has_fdf_manufacture: boolean;
  has_api_manufacture: boolean;
  is_packaging_only: boolean;
  duns_number?: string;
  agent_duns?: string;
  match_score?: number | null;
}

interface ManufacturerImportRefusal {
  id: number;
  fei_number?: string;
  firm_legal_name?: string;
  firm_address?: string;
  product_code?: string;
  product_description?: string;
  refused_date?: string;
  import_division?: string;
  shipment_id?: string;
  fda_sample_analysis?: boolean;
  private_lab_analysis?: boolean;
  refusal_charges_raw?: string;
  refusal_charge_ids?: number[];
  facility_name?: string;
  facility_country?: string;
}

interface ExecutiveDonation {
  recipient_name?: string;
  recipient_party: 'DEM' | 'REP' | 'IND' | 'LIB' | 'GRN' | 'OTH';
  amount_usd: number;
  election_cycle?: number;
  donation_date?: string;
}

interface ManufacturerExecutive {
  id: number;
  full_name: string;
  title?: string;
  source?: string;
  source_url?: string;
  is_current: boolean;
  donations_dem_usd: number;
  donations_rep_usd: number;
  donations_other_usd: number;
  donations: ExecutiveDonation[];
}

interface CcpMatch {
  id: number;
  match_type: 'company' | 'individual';
  list_source: string;
  listed_name: string;
  matched_name: string;
  match_score: number | null;
  match_method?: string;
  checked_at?: string;
  executive_name?: string;
  executive_title?: string;
  reason?: string;
  programs?: string[];
}

interface CcpEvidence {
  source_key: string;
  signal: boolean;
  confidence?: string;
  summary_text?: string;
  evidence_data?: Record<string, unknown>;
  collected_at?: string;
}

interface PoliticianHolding {
  politician_name: string;
  party?: string;
  chamber?: string;
  state?: string;
  ticker?: string;
  transaction_type?: string;
  amount_range?: string;
  transaction_date?: string;
  disclosure_date?: string;
  asset_description?: string;
}

interface SubsidiaryEntry {
  id: number;
  name: string;
  country?: string;
  country_iso3?: string;
  product_count: number;
}

interface AggregatedStats {
  product_count: number;
  recall_count: number;
  active_recall_count: number;
  shortage_count: number;
  import_refusal_count: number;
}

interface ManufacturerProfile {
  id: number;
  name: string;
  country?: string;
  country_iso3?: string;
  address?: string;
  dea_number?: string;
  parent_company?: string;
  foreign_parent_company?: string;
  parent_co_duns?: string;
  foreign_parent_co_duns?: string;
  ccp_flag?: boolean;
  ccp_flag_source?: string;
  ccp_flag_note?: string;
  ccp_risk_score?: string;
  ccp_scoring_notes?: string;
  ccp_scoring_signals?: {
    sources_fired?: string[];
    sources_checked?: number;
    highest_confidence?: string;
  };
  ccp_scorer_method?: string;
  is_public?: boolean | null;
  stock_ticker?: string;
  stock_exchange?: string;
  product_count: number;
  recall_count: number;
  active_recall_count: number;
  shortage_count: number;
  products: ManufacturerProduct[];
  recalls: ManufacturerRecall[];
  shortages: ManufacturerShortage[];
  facilities: ManufacturerFacility[];
  import_refusals: ManufacturerImportRefusal[];
  executives: ManufacturerExecutive[];
  parent_executives?: ManufacturerExecutive[];
  executives_inherited_from?: string | null;
  parent_manufacturer_id?: number | null;
  parent_manufacturer_name?: string | null;
  subsidiaries?: SubsidiaryEntry[];
  aggregated_stats?: AggregatedStats | null;
  subsidiary_products?: SubsidiaryProductGroup[];
  politician_holdings: PoliticianHolding[];
  ccp_matches?: CcpMatch[];
  ccp_evidence?: CcpEvidence[];
  website?: string;
  phone?: string;
  email?: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────



function recallClassColor(classification: string | undefined, T: Tokens): string {
  if (!classification) return T.textLight;
  const c = classification.toLowerCase();
  if (/class\s*i[^iv]/.test(c) || c === "class i") return T.danger;
  if (/class\s*ii[^i]/.test(c) || c === "class ii") return T.warning;
  return "#6B7280";
}

function recallStatusColor(status: string | undefined, T: Tokens): string {
  if (!status) return T.textLight;
  return status.toLowerCase() === "ongoing" ? T.danger : "#6B7280";
}

function shortageStatusColor(status: string | undefined, T: Tokens): string {
  if (!status) return T.textLight;
  const s = status.toLowerCase();
  if (s === "current") return T.danger;
  if (s === "resolved") return T.success;
  return T.warning;
}

function shortageSeverityColor(severity: string | undefined, T: Tokens): string {
  if (!severity) return T.textLight;
  const s = severity.toLowerCase();
  if (s === "high" || s === "critical") return T.danger;
  if (s === "medium" || s === "moderate") return T.warning;
  return T.success;
}

function formatDate(dateStr?: string): string {
  if (!dateStr) return "";
  try { return new Date(dateStr).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" }); }
  catch { return dateStr; }
}

function FieldRow({ label, value }: { label: string; value?: string | null }) {
  const { T } = useTheme();
  if (!value) return null;
  return (
    <div style={{ display: "flex", gap: 12, fontSize: 12 }}>
      <span style={{ minWidth: 120, fontWeight: 700, color: T.textLight, flexShrink: 0 }}>{label}</span>
      <span style={{ color: T.textDark }}>{value}</span>
    </div>
  );
}

function SectionCard({ title, children }: { title: string; children: React.ReactNode }) {
  const { T } = useTheme();
  return (
    <div style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "16px 20px" }}>
      <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 12 }}>
        {title}
      </div>
      <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>{children}</div>
    </div>
  );
}

// ── Tab — Overview ────────────────────────────────────────────────────────────

function TabOverview({ m, onViewManufacturer }: { m: ManufacturerProfile; onViewManufacturer?: (id: number) => void }) {
  const { T } = useTheme();
  const { isMobile, isTablet } = useResponsive();
  const subsidiaries = m.subsidiaries || [];
  const agg          = m.aggregated_stats ?? null;
  const activeShortages = (m.shortages || []).filter(s => s.shortage_status?.toLowerCase() === 'current').length;
  const stats = [
    { label: "Products",         value: m.product_count,       color: T.navy                                                  },
    { label: "Total Recalls",    value: m.recall_count,        color: m.recall_count > 0 ? T.warning : T.textLight            },
    { label: "Active Recalls",   value: m.active_recall_count, color: m.active_recall_count > 0 ? T.danger : T.success        },
    { label: "Shortages",        value: m.shortage_count,      color: m.shortage_count > 0 ? T.warning : T.textLight          },
    { label: "Active Shortages", value: activeShortages,       color: activeShortages > 0 ? T.danger : T.success              },
  ];

  const facilityCount = (m.facilities || []).length;
  const refusalCount  = (m.import_refusals || []).length;
  const apiSites      = (m.facilities || []).filter(f => f.has_api_manufacture).length;
  const fdfSites      = (m.facilities || []).filter(f => f.has_fdf_manufacture).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Stats grid */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : isTablet ? "repeat(2, 1fr)" : "repeat(5, 1fr)", gap: 12 }}>
        {stats.map(s => (
          <div key={s.label} style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: T.radiusSm, padding: "16px 18px", textAlign: "center",
            borderTop: `3px solid ${s.color}`,
          }}>
            <div style={{ fontSize: 28, fontWeight: 900, color: s.color, lineHeight: 1.1 }}>{s.value}</div>
            <div style={{ fontSize: 10, fontWeight: 600, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em", marginTop: 4 }}>{s.label}</div>
          </div>
        ))}
      </div>

      {/* Company details card */}
      <SectionCard title="Company Details">
        <FieldRow label="Country"       value={m.country}         />
        <FieldRow label="Country Code"  value={m.country_iso3}    />
        <FieldRow label="Address"       value={m.address}         />
        <FieldRow label="DEA Number"    value={m.dea_number}      />
        {m.website && <FieldRow label="Website" value={m.website} />}
        {m.phone && <FieldRow label="Phone" value={m.phone} />}
        {m.email && <FieldRow label="Email" value={m.email} />}
      </SectionCard>

      {/* Linked parent company (FK-based, clickable) */}
      {m.parent_manufacturer_id && m.parent_manufacturer_name && (
        <SectionCard title="Parent Company">
          <div
            onClick={() => onViewManufacturer?.(m.parent_manufacturer_id!)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", borderRadius: T.radiusSm,
              background: "#F8FAFC", border: `1px solid ${T.border}`,
              cursor: "pointer",
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.navy }}>{m.parent_manufacturer_name}</div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>View full profile →</div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* Corporate ownership card (shown if any data exists) */}
      {(m.parent_company || m.foreign_parent_company) && (
        <SectionCard title="Corporate Structure">
          <FieldRow label="Parent Company"         value={m.parent_company}          />
          <FieldRow label="Parent DUNS"            value={m.parent_co_duns}          />
          <FieldRow label="Foreign Parent"         value={m.foreign_parent_company}  />
          <FieldRow label="Foreign Parent DUNS"    value={m.foreign_parent_co_duns}  />
        </SectionCard>
      )}

      {/* Subsidiaries & Divisions */}
      {subsidiaries.length > 0 && (
        <SectionCard title={`Subsidiaries & Divisions (${subsidiaries.length})`}>
          {agg && (
            <div style={{
              display: "flex", gap: 16, flexWrap: "wrap",
              padding: "8px 0", marginBottom: 10,
              borderBottom: `1px solid ${T.border}`, fontSize: 12,
            }}>
              <span>
                <strong>{m.product_count}</strong> direct
                {" + "}
                <strong>{agg.product_count - m.product_count}</strong> from subsidiaries
                {" = "}
                <strong style={{ color: T.navy }}>{agg.product_count}</strong> total products
              </span>
              <span><strong>{agg.active_recall_count}</strong> active recalls</span>
              <span><strong>{agg.shortage_count}</strong> shortages</span>
              <span><strong>{agg.import_refusal_count}</strong> import refusals</span>
              <span style={{ fontSize: 11, color: T.textLight }}>(combined across all entities)</span>
            </div>
          )}
          {subsidiaries.map(s => (
            <div
              key={s.id}
              onClick={() => onViewManufacturer?.(s.id)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 10px", borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, background: "#FAFAFA",
                cursor: "pointer", marginBottom: 4,
              }}
            >
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{s.name}</span>
                {s.country && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: T.textLight }}>{s.country}</span>
                )}
              </div>
              <span style={{ fontSize: 11, color: T.textMid, fontWeight: 600 }}>
                {s.product_count} products →
              </span>
            </div>
          ))}
        </SectionCard>
      )}

      {/* FDA registration summary (shown if facilities enrichment ran) */}
      {facilityCount > 0 && (
        <SectionCard title="FDA Registration Summary">
          <div style={{ display: "flex", gap: 20, flexWrap: "wrap" }}>
            <span style={{ fontSize: 12, color: T.textDark }}>
              <strong style={{ color: T.navy }}>{facilityCount}</strong>
              {" "}registered site{facilityCount !== 1 ? "s" : ""}
            </span>
            {fdfSites > 0 && (
              <span style={{ fontSize: 12, color: T.textDark }}>
                <strong style={{ color: "#10B981" }}>{fdfSites}</strong> FDF manufacture
              </span>
            )}
            {apiSites > 0 && (
              <span style={{ fontSize: 12, color: T.textDark }}>
                <strong style={{ color: T.navy }}>{apiSites}</strong> API manufacture
              </span>
            )}
            {refusalCount > 0 && (
              <span style={{ fontSize: 12, color: T.danger, fontWeight: 700 }}>
                {refusalCount} import refusal{refusalCount !== 1 ? "s" : ""}
              </span>
            )}
          </div>
        </SectionCard>
      )}
    </div>
  );
}

// ── Tab — Products ────────────────────────────────────────────────────────────

function ProductRow({ p, onViewProduct }: { p: ManufacturerProduct; onViewProduct: (id: number) => void }) {
  const { T } = useTheme();
  return (
    <div
      onClick={() => onViewProduct(p.id)}
      style={{
        display: "flex", justifyContent: "space-between", alignItems: "center",
        padding: "10px 14px", background: T.white,
        border: `1px solid ${T.border}`, borderRadius: T.radiusSm,
        cursor: "pointer",
      }}
      onMouseEnter={e => (e.currentTarget.style.boxShadow = `0 2px 8px ${T.navy}18`)}
      onMouseLeave={e => (e.currentTarget.style.boxShadow = 'none')}
    >
      <div style={{ flex: 1 }}>
        <div style={{ fontWeight: 700, fontSize: 13, color: T.textDark }}>{p.product_name}</div>
        <div style={{ fontSize: 11, color: T.textMid, marginTop: 3 }}>
          {[p.strength, p.dosage_form, p.ndc_code].filter(Boolean).join(" · ")}
        </div>
      </div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, flexShrink: 0 }}>
        {p.shortage_status && p.shortage_status !== "None" && (
          <span style={{ fontSize: 10, fontWeight: 700, color: T.warning, background: T.warning + '15', border: `1px solid ${T.warning}40`, borderRadius: 99, padding: "2px 8px" }}>
            Shortage
          </span>
        )}
        <span style={{ fontSize: 13, color: T.textLight }}>›</span>
      </div>
    </div>
  );
}

function TabProducts({
  products,
  subsidiaryProducts,
  onViewProduct,
  onViewManufacturer,
}: {
  products: ManufacturerProduct[];
  subsidiaryProducts?: SubsidiaryProductGroup[];
  onViewProduct: (id: number) => void;
  onViewManufacturer?: (id: number) => void;
}) {
  const { T } = useTheme();
  const [openGroups, setOpenGroups] = useState<Set<number>>(new Set());

  const toggleGroup = (id: number) =>
    setOpenGroups(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });

  const hasSubGroups = subsidiaryProducts && subsidiaryProducts.length > 0;

  // ── Flat view (no subsidiaries) ──────────────────────────────────────────────
  if (!hasSubGroups) {
    if (products.length === 0) {
      return <div style={{ textAlign: "center", padding: 40, color: T.textLight, fontSize: 13 }}>No products found for this manufacturer.</div>;
    }
    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
        {products.map(p => <ProductRow key={p.id} p={p} onViewProduct={onViewProduct} />)}
      </div>
    );
  }

  // ── Grouped view (parent company) ────────────────────────────────────────────
  const totalProducts = products.length + subsidiaryProducts.reduce((n, g) => n + g.product_count, 0);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      <div style={{ fontSize: 11, color: T.textLight, marginBottom: 4 }}>
        {totalProducts} products across {1 + subsidiaryProducts.length} entities
      </div>

      {/* Own direct products (if any) */}
      {products.length > 0 && (() => {
        const isOpen = openGroups.has(0);
        return (
          <div style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
            <div
              onClick={() => toggleGroup(0)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 16px", background: "#F8FAFC", cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, transform: isOpen ? "rotate(90deg)" : "rotate(0)", display: "inline-block", transition: "transform 0.15s", color: T.textLight }}>›</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: T.navy }}>Direct products</span>
              </div>
              <span style={{ fontSize: 11, color: T.textMid, fontWeight: 600 }}>{products.length} products</span>
            </div>
            {isOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "8px 8px" }}>
                {products.map(p => <ProductRow key={p.id} p={p} onViewProduct={onViewProduct} />)}
              </div>
            )}
          </div>
        );
      })()}

      {/* Subsidiary groups */}
      {subsidiaryProducts.map(group => {
        const isOpen = openGroups.has(group.subsidiary_id);
        return (
          <div key={group.subsidiary_id} style={{ border: `1px solid ${T.border}`, borderRadius: T.radiusSm, overflow: "hidden" }}>
            <div
              onClick={() => toggleGroup(group.subsidiary_id)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "12px 16px", background: "#F8FAFC", cursor: "pointer",
              }}
            >
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 14, transform: isOpen ? "rotate(90deg)" : "rotate(0)", display: "inline-block", transition: "transform 0.15s", color: T.textLight }}>›</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: T.navy }}>{group.subsidiary_name}</span>
                {onViewManufacturer && (
                  <span
                    onClick={e => { e.stopPropagation(); onViewManufacturer(group.subsidiary_id); }}
                    style={{ fontSize: 10, color: T.navy, fontWeight: 600, textDecoration: "underline dotted", cursor: "pointer" }}
                  >
                    View profile
                  </span>
                )}
              </div>
              <span style={{ fontSize: 11, color: T.textMid, fontWeight: 600 }}>{group.product_count} products</span>
            </div>
            {isOpen && (
              <div style={{ display: "flex", flexDirection: "column", gap: 4, padding: "8px 8px" }}>
                {group.products.map(p => <ProductRow key={p.id} p={p} onViewProduct={onViewProduct} />)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Tab — Recalls ─────────────────────────────────────────────────────────────

function TabRecalls({ recalls }: { recalls: ManufacturerRecall[] }) {
  const { T } = useTheme();
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  if (recalls.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: T.success, fontSize: 13, fontWeight: 600 }}>
        No FDA recalls found for this manufacturer's products.
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {recalls.map((r, idx) => {
        const classColor  = recallClassColor(r.classification, T);
        const statusColor = recallStatusColor(r.status, T);
        const reason  = r.reason_for_recall || "";
        const isLong  = reason.length > 160;
        const isOpen  = !!expanded[idx];
        return (
          <div key={r.event_id || idx} style={{ background: T.white, border: `1px solid ${T.border}`, borderRadius: T.radiusSm, padding: "14px 18px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              {r.classification && (
                <span style={{ fontSize: 10, fontWeight: 800, color: classColor, background: classColor + '15', border: `1px solid ${classColor}40`, borderRadius: 999, padding: "2px 9px", textTransform: "uppercase" }}>
                  {r.classification}
                </span>
              )}
              {r.status && (
                <span style={{ fontSize: 10, fontWeight: 700, color: statusColor, background: statusColor + '12', border: `1px solid ${statusColor}35`, borderRadius: 999, padding: "2px 9px" }}>
                  {r.status}
                </span>
              )}
              {r.recall_number && <span style={{ fontSize: 11, color: T.textLight }}>{r.recall_number}</span>}
              {r.product_name  && <span style={{ fontSize: 11, color: T.navy, fontWeight: 600 }}>{r.product_name}</span>}
            </div>
            {reason && (
              <div style={{ marginBottom: 8, fontSize: 12, color: T.textDark, lineHeight: 1.5 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>Reason: </span>
                {isOpen || !isLong ? reason : `${reason.slice(0, 160)}…`}
                {isLong && (
                  <button onClick={() => setExpanded(prev => ({ ...prev, [idx]: !prev[idx] }))}
                    style={{ marginLeft: 6, background: "none", border: "none", cursor: "pointer", fontSize: 11, color: T.navy, fontWeight: 600, padding: 0 }}>
                    {isOpen ? "▲ Less" : "▼ More"}
                  </button>
                )}
              </div>
            )}
            <div style={{ display: "flex", flexWrap: "wrap", gap: 14, fontSize: 11, color: T.textMid }}>
              {r.recalling_firm          && <span><strong style={{ color: T.textDark }}>Firm:</strong> {r.recalling_firm}</span>}
              {r.recall_initiation_date  && <span><strong style={{ color: T.textDark }}>Initiated:</strong> {formatDate(r.recall_initiation_date)}</span>}
              {r.termination_date        && <span><strong style={{ color: T.textDark }}>Terminated:</strong> {formatDate(r.termination_date)}</span>}
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tab — Shortages ───────────────────────────────────────────────────────────

const SHORTAGE_STATUS_ORDER: Record<string, number> = { current: 0, resolved: 2 };

function sortShortages(shortages: ManufacturerShortage[]): ManufacturerShortage[] {
  return [...shortages].sort((a, b) => {
    const orderA = SHORTAGE_STATUS_ORDER[a.shortage_status?.toLowerCase() ?? ""] ?? 1;
    const orderB = SHORTAGE_STATUS_ORDER[b.shortage_status?.toLowerCase() ?? ""] ?? 1;
    return orderA - orderB;
  });
}

function TabShortages({ shortages, onViewProduct }: { shortages: ManufacturerShortage[]; onViewProduct: (id: number) => void }) {
  const { T } = useTheme();
  if (shortages.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: T.success, fontSize: 13, fontWeight: 600 }}>
        No drug shortages found for this manufacturer's products.
      </div>
    );
  }
  const sorted = sortShortages(shortages);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {sorted.map((s) => {
        const statusColor   = shortageStatusColor(s.shortage_status, T);
        const severityColor = shortageSeverityColor(s.severity, T);
        return (
          <div key={s.id} style={{
            background: T.white,
            border: `1px solid ${s.shortage_status?.toLowerCase() === "current" ? T.danger + '40' : T.border}`,
            borderLeft: `4px solid ${statusColor}`,
            borderRadius: T.radiusSm,
            padding: "14px 18px",
          }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap", marginBottom: 8 }}>
              {s.shortage_status && (
                <span style={{ fontSize: 10, fontWeight: 800, color: statusColor, background: statusColor + '15', border: `1px solid ${statusColor}40`, borderRadius: 999, padding: "2px 9px", textTransform: "uppercase" }}>
                  {s.shortage_status}
                </span>
              )}
              {s.severity && (
                <span style={{ fontSize: 10, fontWeight: 700, color: severityColor, background: severityColor + '12', border: `1px solid ${severityColor}35`, borderRadius: 999, padding: "2px 9px" }}>
                  {s.severity} severity
                </span>
              )}
              {s.product_name && s.product_id && (
                <span onClick={() => onViewProduct(s.product_id!)}
                  style={{ fontSize: 12, color: T.navy, fontWeight: 700, cursor: "pointer", textDecoration: "underline dotted" }}
                  title="View product detail">
                  {s.product_name}
                </span>
              )}
              {s.ndc_code && <span style={{ fontSize: 11, color: T.textLight }}>{s.ndc_code}</span>}
            </div>
            {s.reason && (
              <div style={{ fontSize: 12, color: T.textDark, lineHeight: 1.5, marginBottom: 6 }}>
                <span style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.06em" }}>Reason: </span>
                {s.reason}
              </div>
            )}
            {s.reported_date && (
              <div style={{ fontSize: 11, color: T.textMid }}>
                <strong style={{ color: T.textDark }}>Reported:</strong> {formatDate(s.reported_date)}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}

// ── Tab — FDA Sites (facilities only) ─────────────────────────────────────────

function operationsBadges(f: ManufacturerFacility, T: Tokens) {
  const badges = [];
  if (f.has_api_manufacture) badges.push({ label: "API Mfg", color: T.navy });
  if (f.has_fdf_manufacture) badges.push({ label: "FDF Mfg", color: "#10B981" });
  if (f.is_packaging_only)   badges.push({ label: "Packaging", color: "#6B7280" });
  return badges;
}

function TabFdaSites({ facilities }: {
  facilities: ManufacturerFacility[];
}) {
  const { T } = useTheme();
  if (facilities.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: T.textLight, fontSize: 13 }}>
        No FDA facility records matched to this manufacturer yet.
        <div style={{ fontSize: 11, marginTop: 8, color: T.textLight }}>
          Run <code>python scripts/enrich_manufacturers.py</code> to import facility data.
        </div>
      </div>
    );
  }
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {facilities.map(f => {
        const badges = operationsBadges(f, T);
        return (
          <div key={f.id} style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderLeft: `4px solid ${f.has_api_manufacture ? T.navy : f.has_fdf_manufacture ? "#10B981" : T.border}`,
            borderRadius: T.radiusSm, padding: "14px 18px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap" }}>
              {/* Left */}
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, fontSize: 13, color: T.textDark, marginBottom: 4 }}>{f.firm_name}</div>
                <div style={{ display: "flex", gap: 16, flexWrap: "wrap", fontSize: 11, color: T.textMid }}>
                  <span>{[f.country, f.country_iso3].filter(Boolean).join(" · ")}</span>
                  {f.fei_number  && <span>FEI: <strong style={{ color: T.textDark }}>{f.fei_number}</strong></span>}
                  {f.duns_number && <span>DUNS: <strong style={{ color: T.textDark }}>{f.duns_number}</strong></span>}
                </div>
                {f.operations_raw && (
                  <div style={{ fontSize: 11, color: T.textLight, marginTop: 4 }}>{f.operations_raw}</div>
                )}
              </div>
              {/* Right: operation type badges */}
              <div style={{ display: "flex", gap: 6, flexShrink: 0, flexWrap: "wrap" }}>
                {badges.map(b => (
                  <span key={b.label} style={{
                    fontSize: 10, fontWeight: 700, color: b.color,
                    background: b.color + '15', border: `1px solid ${b.color}40`,
                    borderRadius: 99, padding: "2px 8px",
                  }}>{b.label}</span>
                ))}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// ── Tab — Import Refusals ─────────────────────────────────────────────────────

function TabImportRefusals({ importRefusals }: {
  importRefusals: ManufacturerImportRefusal[];
}) {
  const { T } = useTheme();
  const [expanded, setExpanded] = useState<Record<number, boolean>>({});

  if (importRefusals.length === 0) {
    return (
      <div style={{ textAlign: "center", padding: 40, color: T.success, fontSize: 13, fontWeight: 600 }}>
        No FDA import refusals found for this manufacturer.
      </div>
    );
  }

  // Group by FEI / facility for a cleaner display
  const byFei: Record<string, ManufacturerImportRefusal[]> = {};
  for (const r of importRefusals) {
    const key = r.fei_number || "unknown";
    if (!byFei[key]) byFei[key] = [];
    byFei[key].push(r);
  }
  const feiKeys = Object.keys(byFei).sort();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 20 }}>
      {/* Summary banner */}
      <div style={{
        background: T.danger + '08',
        border: `1px solid ${T.danger}30`,
        borderRadius: T.radiusSm,
        padding: "12px 16px",
        display: "flex", gap: 20, flexWrap: "wrap", alignItems: "center",
      }}>
        <span style={{ fontSize: 13, fontWeight: 800, color: T.danger }}>
          {importRefusals.length} import refusal{importRefusals.length !== 1 ? "s" : ""} on record
        </span>
        <span style={{ fontSize: 12, color: T.textMid }}>
          across <strong style={{ color: T.textDark }}>{feiKeys.length}</strong> site{feiKeys.length !== 1 ? "s" : ""}
        </span>
        <span style={{ fontSize: 11, color: T.textLight }}>Source: FDA Import Refusals Database</span>
      </div>

      {/* One group per FEI / facility */}
      {feiKeys.map(fei => {
        const rows = byFei[fei];
        const firstRow = rows[0];
        const facilityLabel = firstRow.facility_name || firstRow.firm_legal_name || `FEI ${fei}`;

        return (
          <div key={fei} style={{ display: "flex", flexDirection: "column", gap: 6 }}>
            {/* Facility header */}
            <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em" }}>
              {facilityLabel}
              {firstRow.facility_country && ` · ${firstRow.facility_country}`}
              {fei !== "unknown" && <span style={{ marginLeft: 6, color: T.navy }}>FEI {fei}</span>}
              <span style={{ marginLeft: 8, color: T.danger }}>{rows.length} refusal{rows.length !== 1 ? "s" : ""}</span>
            </div>

            {rows.map(r => {
              const desc = r.product_description || r.product_code || "Unknown product";
              const isLong = desc.length > 120;
              const isOpen = !!expanded[r.id];

              return (
                <div key={r.id} style={{
                  background: T.white,
                  border: `1px solid ${T.danger}25`,
                  borderLeft: `4px solid ${T.danger}`,
                  borderRadius: T.radiusSm,
                  padding: "12px 18px",
                }}>
                  {/* Top row: product + date */}
                  <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap", marginBottom: 6 }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 12, color: T.textDark }}>
                        {isOpen || !isLong ? desc : `${desc.slice(0, 120)}…`}
                        {isLong && (
                          <button
                            onClick={() => setExpanded(prev => ({ ...prev, [r.id]: !prev[r.id] }))}
                            style={{ marginLeft: 6, background: "none", border: "none", cursor: "pointer", fontSize: 11, color: T.navy, fontWeight: 600, padding: 0 }}
                          >
                            {isOpen ? "▲ Less" : "▼ More"}
                          </button>
                        )}
                      </div>
                      {r.firm_address && (
                        <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>{r.firm_address}</div>
                      )}
                    </div>
                    {r.refused_date && (
                      <span style={{ fontSize: 11, color: T.danger, fontWeight: 700, flexShrink: 0 }}>
                        {formatDate(r.refused_date)}
                      </span>
                    )}
                  </div>

                  {/* Meta row */}
                  <div style={{ display: "flex", gap: 14, flexWrap: "wrap", fontSize: 11, color: T.textMid }}>
                    {r.import_division      && <span>{r.import_division}</span>}
                    {r.shipment_id          && <span>Shipment: <strong style={{ color: T.textDark }}>{r.shipment_id}</strong></span>}
                    {r.product_code         && <span>Code: {r.product_code}</span>}
                    {r.fda_sample_analysis  && <span style={{ color: T.warning, fontWeight: 600 }}>FDA Sample ✓</span>}
                    {r.private_lab_analysis && <span style={{ color: T.warning, fontWeight: 600 }}>Private Lab ✓</span>}
                  </div>

                  {/* Charge codes */}
                  {r.refusal_charges_raw && (
                    <div style={{ marginTop: 6, fontSize: 11, color: T.textLight }}>
                      <strong style={{ color: T.textDark }}>Charge codes:</strong> {r.refusal_charges_raw}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        );
      })}
    </div>
  );
}

// ── Tab — Ownership ───────────────────────────────────────────────────────────

function TabOwnership({ m, onViewManufacturer }: { m: ManufacturerProfile; onViewManufacturer?: (id: number) => void }) {
  const { T } = useTheme();
  const hasCorpData   = !!(m.parent_company || m.foreign_parent_company);
  const hasPublicData = m.is_public === true && !!m.stock_ticker;
  const holdings      = m.politician_holdings || [];
  const subsidiaries  = m.subsidiaries || [];
  const agg           = m.aggregated_stats ?? null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── Public / Private status ── */}
      <SectionCard title="Company Status">
        {m.is_public === true ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.navy }}>
                Public Company
                {m.stock_ticker && (
                  <span style={{ marginLeft: 8, fontSize: 12, fontWeight: 600, color: T.textMid }}>
                    {m.stock_exchange ? `${m.stock_exchange}:` : ""}{m.stock_ticker}
                  </span>
                )}
              </div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                Source: SEC EDGAR
              </div>
            </div>
          </div>
        ) : m.is_public === false ? (
          <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
            <div>
              <div style={{ fontSize: 14, fontWeight: 700, color: T.textDark }}>Private Company</div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                No SEC filings found — ownership data limited
              </div>
            </div>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: T.textLight }}>
            Public/private status not yet determined. Run <code>python scripts/enrich_executives.py</code>.
          </div>
        )}
      </SectionCard>

      {/* ── Linked parent company (FK, clickable) ── */}
      {m.parent_manufacturer_id && m.parent_manufacturer_name && (
        <SectionCard title="Parent Company">
          <div
            onClick={() => onViewManufacturer?.(m.parent_manufacturer_id!)}
            style={{
              display: "flex", alignItems: "center", gap: 10,
              padding: "10px 12px", borderRadius: T.radiusSm,
              background: "#F8FAFC", border: `1px solid ${T.border}`,
              cursor: onViewManufacturer ? "pointer" : "default",
            }}
          >
            <div style={{ flex: 1 }}>
              <div style={{ fontSize: 14, fontWeight: 800, color: T.navy }}>
                {m.parent_manufacturer_name}
              </div>
              <div style={{ fontSize: 11, color: T.textLight, marginTop: 2 }}>
                View full profile →
              </div>
            </div>
          </div>
        </SectionCard>
      )}

      {/* ── Subsidiaries & Divisions ── */}
      {subsidiaries.length > 0 && (
        <SectionCard title={`Subsidiaries & Divisions (${subsidiaries.length})`}>
          {subsidiaries.map(s => (
            <div
              key={s.id}
              onClick={() => onViewManufacturer?.(s.id)}
              style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "8px 10px", borderRadius: T.radiusSm,
                border: `1px solid ${T.border}`, background: "#FAFAFA",
                cursor: onViewManufacturer ? "pointer" : "default",
                marginBottom: 4,
              }}
            >
              <div>
                <span style={{ fontSize: 13, fontWeight: 600, color: T.navy }}>{s.name}</span>
                {s.country && (
                  <span style={{ marginLeft: 8, fontSize: 11, color: T.textLight }}>{s.country}</span>
                )}
              </div>
              <span style={{ fontSize: 11, color: T.textMid, fontWeight: 600 }}>
                {s.product_count} products →
              </span>
            </div>
          ))}
        </SectionCard>
      )}

      {/* ── Corporate structure (TRI parent company) ── */}
      {hasCorpData && (
        <SectionCard title="Corporate Structure">
          {m.parent_company && (
            <div>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginBottom: 4 }}>
                Domestic Parent Company
              </div>
              <div style={{ fontSize: 14, fontWeight: 900, color: T.navy }}>{m.parent_company}</div>
              {m.parent_co_duns && (
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 3 }}>DUNS: {m.parent_co_duns}</div>
              )}
            </div>
          )}
          {m.foreign_parent_company && (
            <div style={{ marginTop: m.parent_company ? 12 : 0, paddingTop: m.parent_company ? 12 : 0, borderTop: m.parent_company ? `1px solid ${T.border}` : "none" }}>
              <div style={{ fontSize: 12, fontWeight: 700, color: T.textDark, marginBottom: 4 }}>
                Ultimate Foreign Parent
              </div>
              <div style={{ fontSize: 14, fontWeight: 900, color: T.navy }}>{m.foreign_parent_company}</div>
              {m.foreign_parent_co_duns && (
                <div style={{ fontSize: 11, color: T.textLight, marginTop: 3 }}>DUNS: {m.foreign_parent_co_duns}</div>
              )}
            </div>
          )}
          <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.border}`, fontSize: 11, color: T.textLight }}>
            Source: EPA Toxics Release Inventory (TRI) 2024
          </div>
        </SectionCard>
      )}

      {/* ── Congressional stock holdings ── */}
      {hasPublicData && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Congressional Stock Holdings — {m.stock_ticker}
            <span style={{ marginLeft: 8, color: T.navy }}>{holdings.length} transaction{holdings.length !== 1 ? "s" : ""}</span>
          </div>
          {holdings.length === 0 ? (
            <div style={{ textAlign: "center", padding: 28, color: T.success, fontSize: 12, fontWeight: 600 }}>
              No congressional stock transactions found for {m.stock_ticker}.
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
              {holdings.map((h, i) => {
                const partyColor = h.party === "DEM" ? "#3B82F6" : h.party === "REP" ? "#EF4444" : T.textMid;
                const txColor    = h.transaction_type?.toLowerCase().includes("sale") ? T.danger : "#10B981";
                return (
                  <div key={i} style={{
                    background: T.white, border: `1px solid ${T.border}`,
                    borderLeft: `4px solid ${partyColor}`,
                    borderRadius: T.radiusSm, padding: "10px 16px",
                    display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: 12, flexWrap: "wrap",
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{h.politician_name}</div>
                      <div style={{ fontSize: 11, color: T.textMid, marginTop: 2, display: "flex", gap: 8, flexWrap: "wrap" }}>
                        {h.party && <span style={{ color: partyColor, fontWeight: 700 }}>{h.party}</span>}
                        {h.chamber && <span>{h.chamber}</span>}
                        {h.state && <span>{h.state}</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "right", flexShrink: 0 }}>
                      {h.transaction_type && (
                        <div style={{ fontSize: 11, fontWeight: 700, color: txColor }}>{h.transaction_type}</div>
                      )}
                      {h.amount_range && (
                        <div style={{ fontSize: 11, color: T.textDark, fontWeight: 600 }}>{h.amount_range}</div>
                      )}
                      {h.transaction_date && (
                        <div style={{ fontSize: 10, color: T.textLight, marginTop: 2 }}>{formatDate(h.transaction_date)}</div>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          )}
          <div style={{ marginTop: 8, fontSize: 11, color: T.textLight }}>
            Source: STOCK Act disclosures via Capitol Trades / OpenSecrets
          </div>
        </div>
      )}

      {/* Placeholder if nothing available */}
      {!hasCorpData && !hasPublicData && holdings.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 24px", color: T.textLight, fontSize: 13 }}>
          No ownership data available yet. Run the enrichment scripts to populate this section.
        </div>
      )}
    </div>
  );
}

// ── Tab — Leadership ──────────────────────────────────────────────────────────

function PoliticalBar({ demUsd, repUsd, otherUsd }: {
  demUsd: number; repUsd: number; otherUsd: number;
}) {
  const { T } = useTheme();
  const total = demUsd + repUsd + otherUsd;
  if (total === 0) return null;

  const demPct   = Math.round((demUsd   / total) * 100);
  const repPct   = Math.round((repUsd   / total) * 100);
  const otherPct = 100 - demPct - repPct;

  const formatUsd = (n: number) =>
    n >= 1000 ? `$${(n / 1000).toFixed(0)}K` : `$${n.toFixed(0)}`;

  const strongLean = demPct >= 85 ? "Strong DEM lean" : repPct >= 85 ? "Strong REP lean" : null;
  const leanColor  = demPct >= 85 ? "#3B82F6" : "#EF4444";

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
      {/* Bar */}
      <div style={{ display: "flex", height: 22, borderRadius: 11, overflow: "hidden", border: `1px solid ${T.border}` }}>
        {demPct > 0 && (
          <div style={{ width: `${demPct}%`, background: "#3B82F6", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {demPct >= 12 && <span style={{ fontSize: 10, fontWeight: 800, color: "#fff" }}>{demPct}%</span>}
          </div>
        )}
        {otherPct > 0 && (
          <div style={{ width: `${otherPct}%`, background: T.textLight }} />
        )}
        {repPct > 0 && (
          <div style={{ width: `${repPct}%`, background: "#EF4444", display: "flex", alignItems: "center", justifyContent: "center" }}>
            {repPct >= 12 && <span style={{ fontSize: 10, fontWeight: 800, color: "#fff" }}>{repPct}%</span>}
          </div>
        )}
      </div>
      {/* Legend */}
      <div style={{ display: "flex", gap: 16, fontSize: 11, color: T.textMid }}>
        <span style={{ color: "#3B82F6", fontWeight: 700 }}>Democrat {formatUsd(demUsd)}</span>
        <span style={{ color: "#EF4444", fontWeight: 700 }}>Republican {formatUsd(repUsd)}</span>
        {otherUsd > 0 && <span style={{ color: T.textLight }}>Other {formatUsd(otherUsd)}</span>}
        <span style={{ color: T.textLight }}>Total {formatUsd(total)}</span>
      </div>
      {strongLean && (
        <span style={{ fontSize: 10, fontWeight: 800, color: leanColor, background: leanColor + '15',
          border: `1px solid ${leanColor}40`, borderRadius: 99, padding: "2px 10px", alignSelf: "flex-start" }}>
          {strongLean}
        </span>
      )}
    </div>
  );
}

function TabLeadership({ m, onViewManufacturer }: { m: ManufacturerProfile; onViewManufacturer?: (id: number) => void }) {
  const { T } = useTheme();
  const [openExec, setOpenExec] = useState<Record<number, boolean>>({});
  const executives    = m.executives       || [];
  const parentExecs   = m.parent_executives || [];
  const inheritedFrom = m.executives_inherited_from ?? null;
  const hasLocal      = executives.length > 0;
  const hasCorporate  = parentExecs.length > 0 && !!inheritedFrom;

  // Aggregate donations across local executives for political bar
  const totalDem    = executives.reduce((s, e) => s + (e.donations_dem_usd   || 0), 0);
  const totalRep    = executives.reduce((s, e) => s + (e.donations_rep_usd   || 0), 0);
  const totalOther  = executives.reduce((s, e) => s + (e.donations_other_usd || 0), 0);
  const hasAnyDonations = totalDem + totalRep + totalOther > 0;

  /** Renders a list of executive cards (reused for local + corporate sections) */
  const renderExecCards = (list: ManufacturerExecutive[]) => (
    <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
      {list.map(e => {
        const execTotal = e.donations_dem_usd + e.donations_rep_usd + e.donations_other_usd;
        const isOpen = !!openExec[e.id];
        return (
          <div key={e.id} style={{
            background: T.white, border: `1px solid ${T.border}`,
            borderRadius: T.radiusSm, padding: "12px 16px",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", gap: 8 }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>{e.full_name}</div>
                <div style={{ fontSize: 11, color: T.textMid, marginTop: 2 }}>
                  {e.title && <span>{e.title}</span>}
                  {e.source && (
                    <span style={{ marginLeft: 8, color: T.textLight }}>
                      · {e.source === "sec_edgar" ? "SEC EDGAR" : e.source}
                    </span>
                  )}
                </div>
              </div>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                {execTotal > 0 && (
                  <span style={{ fontSize: 11, color: T.textDark, fontWeight: 600 }}>
                    ${execTotal >= 1000 ? `${(execTotal / 1000).toFixed(0)}K` : execTotal.toFixed(0)}
                  </span>
                )}
                {e.donations.length > 0 && (
                  <button
                    onClick={() => setOpenExec(prev => ({ ...prev, [e.id]: !prev[e.id] }))}
                    style={{ background: "none", border: `1px solid ${T.border}`, borderRadius: 6,
                      cursor: "pointer", fontSize: 11, color: T.navy, fontWeight: 600, padding: "3px 8px" }}>
                    {isOpen ? "▲ Hide" : "▼ Donations"}
                  </button>
                )}
              </div>
            </div>
            {isOpen && e.donations.length > 0 && (
              <div style={{ marginTop: 10, paddingTop: 10, borderTop: `1px solid ${T.borderLight}`,
                display: "flex", flexDirection: "column", gap: 4 }}>
                {e.donations.map((d, di) => {
                  const pColor = d.recipient_party === "DEM" ? "#3B82F6" : d.recipient_party === "REP" ? "#EF4444" : T.textLight;
                  return (
                    <div key={di} style={{ display: "flex", justifyContent: "space-between",
                      fontSize: 11, color: T.textMid, flexWrap: "wrap", gap: 4 }}>
                      <span style={{ flex: 1 }}>{d.recipient_name || "—"}</span>
                      <span style={{ color: pColor, fontWeight: 700, minWidth: 32 }}>{d.recipient_party}</span>
                      <span style={{ color: T.textLight, minWidth: 32 }}>{d.election_cycle}</span>
                      <span style={{ fontWeight: 700, color: T.textDark, minWidth: 50, textAlign: "right" }}>
                        ${d.amount_usd >= 1000 ? `${(d.amount_usd / 1000).toFixed(0)}K` : d.amount_usd.toFixed(0)}
                      </span>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>

      {/* ── CCP flag banner + match details ── */}
      {m.ccp_flag && (() => {
        const matches = m.ccp_matches || [];
        const companyMatches = matches.filter(x => x.match_type === "company");
        const individualMatches = matches.filter(x => x.match_type === "individual");
        const listSources = Array.from(new Set(matches.map(x => x.list_source))).map(s => ccpSourceLabel(s));

        return (
          <div style={{
            background: "#FEF2F2", border: `2px solid #EF4444`,
            borderRadius: T.radius, overflow: "hidden",
          }}>
            {/* Header */}
            <div style={{
              background: "#EF4444", padding: "12px 18px",
              display: "flex", alignItems: "center", gap: 10,
            }}>
              <div style={{ flex: 1 }}>
                <div style={{ fontSize: 14, fontWeight: 900, color: "#fff" }}>
                  CCP / Chinese Military Screening Alert
                </div>
                <div style={{ fontSize: 11, color: "rgba(255,255,255,0.85)", marginTop: 2 }}>
                  {matches.length} match{matches.length !== 1 ? "es" : ""} found
                  {listSources.length > 0 && ` · ${listSources.join(", ")}`}
                </div>
              </div>
            </div>

            <div style={{ padding: "14px 18px", display: "flex", flexDirection: "column", gap: 12 }}>
              {/* Existing note */}
              {m.ccp_flag_note && (
                <div style={{ fontSize: 12, color: "#7F1D1D", fontWeight: 600 }}>{bilingualSummary(m.ccp_flag_note)}</div>
              )}

              {/* Company matches */}
              {companyMatches.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B",
                    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                    Company Matches
                  </div>
                  {companyMatches.map(cm => (
                    <div key={cm.id} style={{
                      background: "#fff", border: "1px solid #FECACA", borderRadius: T.radiusSm,
                      padding: "10px 14px", marginBottom: 6,
                      borderLeft: "3px solid #EF4444",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#7F1D1D" }}>
                          {cm.listed_name}
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: 700, color: "#fff", background: "#DC2626",
                          padding: "2px 8px", borderRadius: 10,
                        }}>
                          {ccpSourceLabel(cm.list_source)}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "#991B1B", marginTop: 4 }}>
                        Matched to: <strong>{cm.matched_name}</strong>
                        {cm.match_score != null && (
                          <span style={{ marginLeft: 8, color: "#B91C1C" }}>
                            Score: {(cm.match_score * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {cm.programs && cm.programs.length > 0 && (
                        <div style={{ fontSize: 11, color: "#7F1D1D", marginTop: 4 }}>
                          Programs: {cm.programs.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Individual matches */}
              {individualMatches.length > 0 && (
                <div>
                  <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B",
                    textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                    Individual Matches
                  </div>
                  {individualMatches.map(im => (
                    <div key={im.id} style={{
                      background: "#fff", border: "1px solid #FECACA", borderRadius: T.radiusSm,
                      padding: "10px 14px", marginBottom: 6,
                      borderLeft: "3px solid #F59E0B",
                    }}>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <div style={{ fontSize: 13, fontWeight: 700, color: "#7F1D1D" }}>
                          {im.executive_name || im.matched_name}
                          {im.executive_title && (
                            <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 6, color: "#991B1B" }}>
                              — {im.executive_title}
                            </span>
                          )}
                        </div>
                        <span style={{
                          fontSize: 10, fontWeight: 700, color: "#fff", background: "#F59E0B",
                          padding: "2px 8px", borderRadius: 10,
                        }}>
                          {ccpSourceLabel(im.list_source)}
                        </span>
                      </div>
                      <div style={{ fontSize: 11, color: "#991B1B", marginTop: 4 }}>
                        Listed as: <strong>{im.listed_name}</strong>
                        {im.match_score != null && (
                          <span style={{ marginLeft: 8, color: "#B91C1C" }}>
                            Score: {(im.match_score * 100).toFixed(0)}%
                          </span>
                        )}
                      </div>
                      {im.reason && (
                        <div style={{ fontSize: 11, color: "#7F1D1D", marginTop: 4, fontStyle: "italic" }}>
                          {im.reason}
                        </div>
                      )}
                      {im.programs && im.programs.length > 0 && (
                        <div style={{ fontSize: 11, color: "#7F1D1D", marginTop: 4 }}>
                          Programs: {im.programs.join(", ")}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              )}

              {/* Fallback if flag but no matches loaded yet */}
              {matches.length === 0 && (
                <div style={{ fontSize: 12, color: "#991B1B", fontWeight: 600 }}>
                  Source: {(m.ccp_flag_source || "").split(",").map(s => ccpSourceLabel(s.trim())).filter(Boolean).join(", ") || "CCP Screening"} · Classified as high-risk supplier
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* ── Company status ── */}
      <SectionCard title="Company Status">
        {m.is_public === true ? (
          <div style={{ fontSize: 13, color: T.textDark }}>
            <strong>Public Company</strong>
            {m.stock_ticker && (
              <span style={{ marginLeft: 8, color: T.navy, fontWeight: 700 }}>
                {m.stock_exchange ? `${m.stock_exchange}: ` : ""}{m.stock_ticker}
              </span>
            )}
          </div>
        ) : m.is_public === false ? (
          <div style={{ fontSize: 13, color: T.textDark }}>
            <strong>Private Company</strong>
            <span style={{ marginLeft: 8, fontSize: 11, color: T.textLight }}>
              Executive data sourced from public filings, LinkedIn, and state records
            </span>
          </div>
        ) : (
          <div style={{ fontSize: 12, color: T.textLight }}>
            Status unknown — run <code>python scripts/enrich_executives.py</code>
          </div>
        )}
      </SectionCard>

      {/* ── Section 1: Local Executives ── */}
      <div>
        <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight,
          textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
          {hasCorporate ? "Local Executives & Directors" : "Executives & Directors"}
          {hasLocal && (
            <span style={{ marginLeft: 8, color: T.navy }}>{executives.length} listed</span>
          )}
        </div>

        {!hasLocal && hasCorporate && (
          <div style={{
            background: "#F0F9FF", border: "1px solid #BAE6FD",
            borderRadius: T.radiusSm, padding: "8px 12px",
            fontSize: 12, color: "#0369A1",
            display: "flex", alignItems: "center", gap: 6, marginBottom: 10,
          }}>
            No local executives — corporate leadership from{" "}
            <strong>{inheritedFrom}</strong> is shown below
          </div>
        )}

        {!hasLocal && !hasCorporate && (
          <div style={{ textAlign: "center", padding: "32px 16px", color: T.textLight, fontSize: 12 }}>
            No executive data yet.
            {m.is_public === false
              ? " Private company — data requires manual research or LinkedIn."
              : " Run python scripts/enrich_executives.py to populate."}
          </div>
        )}

        {hasLocal && renderExecCards(executives)}
      </div>

      {/* ── Section 2: Corporate Leadership (Parent) ── */}
      {hasCorporate && (
        <div>
          <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight,
            textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 10 }}>
            Corporate Leadership
            {m.parent_manufacturer_id && onViewManufacturer && (
              <span
                onClick={() => onViewManufacturer(m.parent_manufacturer_id!)}
                style={{ marginLeft: 8, fontSize: 11, fontWeight: 600,
                  color: T.navy, cursor: "pointer", textDecoration: "underline" }}>
                {inheritedFrom} →
              </span>
            )}
            <span style={{ marginLeft: 8, color: T.navy }}>{parentExecs.length} listed</span>
          </div>
          {renderExecCards(parentExecs)}
        </div>
      )}

      {/* ── Political influence summary (local executives only) ── */}
      {hasAnyDonations && (
        <SectionCard title="Political Influence — FEC Campaign Donations">
          <PoliticalBar demUsd={totalDem} repUsd={totalRep} otherUsd={totalOther} />
          <div style={{ marginTop: 8, fontSize: 11, color: T.textLight }}>
            Source: FEC.gov — individual contributions by identified executives
          </div>
        </SectionCard>
      )}

      {/* Placeholder when nothing at all */}
      {!hasLocal && !hasCorporate && !m.ccp_flag && (
        <div style={{ textAlign: "center", padding: "16px 24px", color: T.textLight, fontSize: 12 }}>
          Run enrichment scripts to populate leadership and political data.
        </div>
      )}
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

interface ManufacturerDetailProps {
  manufacturerId: number | null;
  apiBase: string;
  onBack: () => void;
  onViewProduct: (id: number) => void;
  onViewManufacturer?: (id: number) => void;
  initialTab?: Tab;
  IconArrowLeft: React.ComponentType;
  IconSpinner: React.ComponentType;
  ScoreRing: React.ComponentType<{ score: number; size?: number; strokeWidth?: number; color?: string; label?: string }>;
}

type Tab = 'overview' | 'products' | 'recalls' | 'shortages' | 'fda-sites' | 'import-refusals' | 'ownership' | 'leadership' | 'ccp';

const TABS: { id: Tab; label: string }[] = [
  { id: 'overview',         label: 'Overview'         },
  { id: 'products',         label: 'Products'         },
  { id: 'recalls',          label: 'Recalls'          },
  { id: 'shortages',        label: 'Shortages'        },
  { id: 'fda-sites',        label: 'FDA'              },
  { id: 'import-refusals',  label: 'Import Refusals'  },
  { id: 'ownership',        label: 'Ownership'        },
  { id: 'leadership',       label: 'Leadership'       },
  { id: 'ccp',              label: 'CCP'              },
];

export default function ManufacturerDetail({
  manufacturerId,
  apiBase,
  onBack,
  onViewProduct,
  onViewManufacturer,
  initialTab,
  IconArrowLeft,
  IconSpinner,
  ScoreRing,
}: ManufacturerDetailProps) {
  const { T } = useTheme();
  const { isMobile } = useResponsive();
  const [profile, setProfile]   = useState<ManufacturerProfile | null>(null);
  const [loading, setLoading]   = useState(true);
  const [activeTab, setActiveTab] = useState<Tab>(initialTab || 'overview');

  useEffect(() => {
    if (!manufacturerId) return;
    setLoading(true);
    setActiveTab(initialTab || 'overview');
    fetch(`${apiBase}/metasupply/manufacturers/${manufacturerId}`, {
      credentials: 'include',
    })
      .then(r => r.json())
      .then(data => { setProfile(data); setLoading(false); })
      .catch(err => { console.error('Failed to load manufacturer:', err); setLoading(false); });
  }, [manufacturerId, apiBase]);

  if (loading) {
    return (
      <div style={{ display: "flex", justifyContent: "center", padding: 60, color: T.textLight }}>
        <IconSpinner />
      </div>
    );
  }

  if (!profile) {
    return <div style={{ textAlign: "center", padding: 60, color: T.danger }}>Manufacturer not found.</div>;
  }

  const hasActiveRecalls  = profile.active_recall_count > 0;
  const facilitiesCount   = (profile.facilities || []).length;
  const refusalsCount     = (profile.import_refusals || []).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {/* Back button */}
      <button
        onClick={onBack}
        style={{ display: "flex", alignItems: "center", gap: 6, background: "none", border: "none", cursor: "pointer", color: T.textLight, fontSize: 12, fontWeight: 600, padding: "0 0 16px 0", width: "fit-content" }}
      >
        <IconArrowLeft /> Back to Manufacturers
      </button>

      {/* Header card */}
      <div style={{
        background: T.white,
        border: `1px solid ${hasActiveRecalls ? T.danger + '50' : T.border}`,
        borderRadius: T.radius,
        padding: "20px 24px",
        marginBottom: 4,
        boxShadow: hasActiveRecalls ? `0 2px 8px ${T.danger}15` : undefined,
      }}>
        <div style={{ display: "flex", flexDirection: isMobile ? "column" : "row", alignItems: isMobile ? "stretch" : "flex-start", gap: 14 }}>
          <div style={{ flex: 1 }}>
            <div style={{ fontSize: 22, fontWeight: 900, color: T.textDark, lineHeight: 1.1 }}>{profile.name}</div>
            <div style={{ marginTop: 6, display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center" }}>
              {profile.country && (
                <span style={{ fontSize: 12, color: T.textMid }}>{profile.country}</span>
              )}
              {profile.parent_company && (
                <span style={{ fontSize: 12, color: T.textMid }}>·&nbsp;Parent: <strong style={{ color: T.navy }}>{profile.parent_company}</strong></span>
              )}
              <span style={{ fontSize: 11, color: T.textLight }}>·</span>
              <span style={{ fontSize: 12, color: T.textMid }}>
                <strong style={{ color: T.navy }}>{profile.product_count}</strong> product{profile.product_count !== 1 ? 's' : ''}
              </span>
              {profile.recall_count > 0 && (
                <>
                  <span style={{ fontSize: 11, color: T.textLight }}>·</span>
                  <span style={{ fontSize: 12, color: hasActiveRecalls ? T.danger : T.textMid, fontWeight: hasActiveRecalls ? 700 : 400 }}>
                    {profile.recall_count} recall{profile.recall_count !== 1 ? 's' : ''}
                    {profile.active_recall_count > 0 && ` (${profile.active_recall_count} active)`}
                  </span>
                </>
              )}
              {facilitiesCount > 0 && (
                <>
                  <span style={{ fontSize: 11, color: T.textLight }}>·</span>
                  <span style={{ fontSize: 12, color: T.textMid }}>{facilitiesCount} FDA site{facilitiesCount !== 1 ? 's' : ''}</span>
                </>
              )}
            </div>
          </div>

          {/* CCP Alert badge — shown when manufacturer is CCP-flagged */}
          {profile.ccp_flag && (
            <div style={{
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center",
              padding: "12px 18px", flexShrink: 0,
              background: "linear-gradient(135deg, #FEF2F2 0%, #FEE2E2 100%)",
              border: `2px solid ${T.danger}50`,
              borderRadius: T.radiusSm,
              gap: 4,
            }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: T.danger, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                CCP Alert
              </div>
              <div style={{ fontSize: 11, fontWeight: 800, color: T.danger, textTransform: "uppercase", letterSpacing: "0.1em" }}>
                High Risk
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Navigation Tab Bar */}
      <div style={{
        display: "flex",
        flexWrap: "wrap",
        gap: 0,
        background: T.offWhite,
        borderRadius: T.radiusSm,
        padding: 3,
        marginBottom: 20, marginTop: 12,
        width: "fit-content",
      }}>
        {TABS.map(tab => {
          const isActive = activeTab === tab.id;
          let badgeCount: number | null = null;
          let badgeColor = T.danger;
          if (tab.id === 'recalls'          && profile.recall_count > 0)   { badgeCount = profile.recall_count;   badgeColor = T.danger; }
          if (tab.id === 'shortages'        && profile.shortage_count > 0) { badgeCount = profile.shortage_count; badgeColor = T.warning; }
          if (tab.id === 'fda-sites'        && facilitiesCount > 0)        { badgeCount = facilitiesCount;        badgeColor = T.navy; }
          if (tab.id === 'import-refusals'  && refusalsCount > 0)          { badgeCount = refusalsCount;          badgeColor = T.danger; }

          const showCcpFlag = tab.id === 'ccp' && profile.ccp_flag;

          return (
            <button
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              style={{
                display: "flex", alignItems: "center", gap: 6,
                padding: "8px 16px",
                background: isActive ? T.navy : "transparent",
                border: "none",
                borderRadius: 4,
                cursor: "pointer",
                transition: "all 0.15s",
                fontSize: 11,
                fontWeight: isActive ? 700 : 600,
                textTransform: "uppercase" as const,
                letterSpacing: "0.05em",
                color: isActive ? "#fff" : T.textMid,
                whiteSpace: "nowrap" as const,
              }}
              onMouseEnter={e => { if (!isActive) { e.currentTarget.style.background = `${T.navy}10`; } }}
              onMouseLeave={e => { if (!isActive) { e.currentTarget.style.background = "transparent"; } }}
            >
              {tab.label}
              {badgeCount !== null && (
                <span style={{
                  fontSize: 9, fontWeight: 800,
                  color: isActive ? T.navy : "#fff",
                  background: isActive ? "rgba(255,255,255,0.9)" : badgeColor,
                  borderRadius: 99, padding: "1px 6px",
                  minWidth: 16, textAlign: "center" as const,
                }}>
                  {badgeCount}
                </span>
              )}
              {showCcpFlag && (
                <span style={{
                  fontSize: 9, fontWeight: 800,
                  color: "#fff",
                  background: T.danger,
                  borderRadius: 99, padding: "1px 6px",
                  textTransform: "uppercase" as const,
                }}>
                  !
                </span>
              )}
            </button>
          );
        })}
      </div>

      {/* Tab content */}
      {activeTab === 'overview'        && <TabOverview m={profile} onViewManufacturer={onViewManufacturer} />}
      {activeTab === 'products'        && <TabProducts products={profile.products} subsidiaryProducts={profile.subsidiary_products} onViewProduct={onViewProduct} onViewManufacturer={onViewManufacturer} />}
      {activeTab === 'recalls'         && <TabRecalls recalls={profile.recalls} />}
      {activeTab === 'shortages'       && <TabShortages shortages={profile.shortages || []} onViewProduct={onViewProduct} />}
      {activeTab === 'fda-sites'       && <TabFdaSites facilities={profile.facilities || []} />}
      {activeTab === 'import-refusals' && <TabImportRefusals importRefusals={profile.import_refusals || []} />}
      {activeTab === 'ownership'       && <TabOwnership m={profile} onViewManufacturer={onViewManufacturer} />}
      {activeTab === 'leadership'      && <TabLeadership m={profile} onViewManufacturer={onViewManufacturer} />}
      {activeTab === 'ccp'              && (() => {
        const evidence = profile.ccp_evidence || [];
        const firedEvidence = evidence.filter(e => e.signal);
        const uncheckedEvidence = evidence.filter(e => !e.signal);
        const matches = profile.ccp_matches || [];
        const companyMatches = matches.filter(x => x.match_type === "company");
        const individualMatches = matches.filter(x => x.match_type === "individual");

        // Group fired evidence by category
        const grouped: Record<string, CcpEvidence[]> = {};
        for (const ev of firedEvidence) {
          const cat = ccpSourceCategory(ev.source_key);
          if (!grouped[cat]) grouped[cat] = [];
          grouped[cat].push(ev);
        }

        const confidenceColor = (c?: string) =>
          c === "HIGH" ? T.danger : c === "MEDIUM" ? "#D97706" : c === "LOW" ? T.success : T.textLight;

        return (
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            {/* Scoring summary card */}
            <SectionCard title="CCP Connection Analysis">
              {profile.ccp_flag ? (
                <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                  {/* Risk score header */}
                  <div style={{
                    display: "flex", alignItems: "center", gap: 12,
                    background: "#FEF2F2", border: "1px solid #FECACA",
                    borderRadius: T.radiusSm, padding: "14px 18px",
                  }}>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: 14, fontWeight: 800, color: "#7F1D1D" }}>
                        CCP Risk Score: <span style={{ color: T.danger }}>{profile.ccp_risk_score || "HIGH"}</span>
                      </div>
                      {profile.ccp_scorer_method && (
                        <div style={{ fontSize: 10, color: "#991B1B", marginTop: 2 }}>
                          Scorer: {profile.ccp_scorer_method}
                          {profile.ccp_scorer_method === "DETERMINISTIC" && " — Hard floor enforced in code, not policy"}
                        </div>
                      )}
                    </div>
                    <div style={{
                      padding: "4px 12px", borderRadius: 99,
                      background: T.danger, color: "#fff",
                      fontSize: 11, fontWeight: 800, textTransform: "uppercase",
                    }}>
                      {profile.ccp_risk_score || "HIGH"} RISK
                    </div>
                  </div>

                  {/* Scoring notes from ccp_scoring_results */}
                  {profile.ccp_scoring_notes && (
                    <div style={{
                      padding: "12px 16px", background: T.offWhite,
                      borderRadius: T.radiusSm, border: `1px solid ${T.border}`,
                    }}>
                      <div style={{ fontSize: 10, fontWeight: 700, color: T.textLight, textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                        Analysis Summary
                      </div>
                      <div style={{ fontSize: 13, color: T.textDark, lineHeight: 1.6 }}>
                        {profile.ccp_scoring_notes}
                      </div>
                    </div>
                  )}

                  {/* Sources fired summary */}
                  {profile.ccp_scoring_signals?.sources_fired && profile.ccp_scoring_signals.sources_fired.length > 0 && (
                    <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                      <span style={{ fontSize: 10, fontWeight: 700, color: T.textLight, alignSelf: "center", marginRight: 4 }}>
                        SOURCES ({profile.ccp_scoring_signals.sources_fired.length} of {profile.ccp_scoring_signals.sources_checked || "—"}):
                      </span>
                      {profile.ccp_scoring_signals.sources_fired.map(src => (
                        <span key={src} style={{
                          fontSize: 10, fontWeight: 700, padding: "2px 8px", borderRadius: 99,
                          background: "#DC262615", color: "#DC2626", border: "1px solid #DC262630",
                        }}>
                          {ccpSourceLabel(src)}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              ) : (
                <div style={{ display: "flex", flexDirection: "column", alignItems: "center", padding: "32px 24px", gap: 10 }}>
                  <div style={{ fontSize: 13, fontWeight: 600, color: T.success }}>No CCP connection identified</div>
                  <div style={{ fontSize: 11, color: T.textLight, textAlign: "center", maxWidth: 320 }}>
                    This manufacturer has not been flagged for any CCP or Chinese military connections.
                  </div>
                </div>
              )}
            </SectionCard>

            {/* Evidence cards grouped by category */}
            {Object.keys(grouped).length > 0 && (
              <SectionCard title="Evidence by Source Category">
                <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
                  {Object.entries(grouped).map(([category, items]) => (
                    <div key={category}>
                      <div style={{
                        fontSize: 11, fontWeight: 800, color: T.navy,
                        textTransform: "uppercase", letterSpacing: "0.06em",
                        marginBottom: 8, paddingBottom: 4, borderBottom: `2px solid ${T.border}`,
                      }}>
                        {category}
                      </div>
                      <div style={{ display: "flex", flexDirection: "column", gap: 6 }}>
                        {items.map(ev => (
                          <div key={ev.source_key} style={{
                            background: "#fff", border: "1px solid #FECACA",
                            borderRadius: T.radiusSm, padding: "10px 14px",
                            borderLeft: `3px solid ${confidenceColor(ev.confidence)}`,
                          }}>
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                              <div style={{ fontSize: 13, fontWeight: 700, color: T.textDark }}>
                                {ccpSourceLabel(ev.source_key)}
                              </div>
                              <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                                {ev.confidence && (
                                  <span style={{
                                    fontSize: 9, fontWeight: 800, padding: "2px 6px", borderRadius: 99,
                                    background: `${confidenceColor(ev.confidence)}15`,
                                    color: confidenceColor(ev.confidence),
                                    border: `1px solid ${confidenceColor(ev.confidence)}40`,
                                  }}>
                                    {ev.confidence}
                                  </span>
                                )}
                                <span style={{
                                  fontSize: 9, fontWeight: 700, padding: "2px 6px", borderRadius: 99,
                                  background: "#DC262615", color: "#DC2626",
                                }}>
                                  SIGNAL
                                </span>
                              </div>
                            </div>
                            {ev.summary_text && (
                              <div style={{ fontSize: 12, color: T.textMid, marginTop: 4, lineHeight: 1.5 }}>
                                {bilingualSummary(ev.summary_text)}
                              </div>
                            )}
                            {ev.collected_at && (
                              <div style={{ fontSize: 10, color: T.textLight, marginTop: 4 }}>
                                Collected: {new Date(ev.collected_at).toLocaleDateString("en-US", { year: "numeric", month: "short", day: "numeric" })}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    </div>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Company & Individual match cards (from ccp_matches) */}
            {(companyMatches.length > 0 || individualMatches.length > 0) && (
              <SectionCard title="Screening Matches">
                <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
                  {companyMatches.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                        Company Matches
                      </div>
                      {companyMatches.map(cm => (
                        <div key={cm.id} style={{
                          background: "#fff", border: "1px solid #FECACA", borderRadius: T.radiusSm,
                          padding: "10px 14px", marginBottom: 6, borderLeft: "3px solid #EF4444",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#7F1D1D" }}>{cm.listed_name}</div>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#DC2626", padding: "2px 8px", borderRadius: 10 }}>
                              {ccpSourceLabel(cm.list_source)}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: "#991B1B", marginTop: 4 }}>
                            Matched to: <strong>{cm.matched_name}</strong>
                            {cm.match_score != null && <span style={{ marginLeft: 8 }}>Score: {(cm.match_score * 100).toFixed(0)}%</span>}
                          </div>
                          {cm.programs && cm.programs.length > 0 && (
                            <div style={{ fontSize: 11, color: "#7F1D1D", marginTop: 4 }}>Programs: {cm.programs.join(", ")}</div>
                          )}
                        </div>
                      ))}
                    </div>
                  )}
                  {individualMatches.length > 0 && (
                    <div>
                      <div style={{ fontSize: 10, fontWeight: 700, color: "#991B1B", textTransform: "uppercase", letterSpacing: "0.08em", marginBottom: 6 }}>
                        Individual Matches
                      </div>
                      {individualMatches.map(im => (
                        <div key={im.id} style={{
                          background: "#fff", border: "1px solid #FECACA", borderRadius: T.radiusSm,
                          padding: "10px 14px", marginBottom: 6, borderLeft: "3px solid #F59E0B",
                        }}>
                          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                            <div style={{ fontSize: 13, fontWeight: 700, color: "#7F1D1D" }}>
                              {im.executive_name || im.matched_name}
                              {im.executive_title && <span style={{ fontWeight: 400, fontSize: 11, marginLeft: 6, color: "#991B1B" }}>— {im.executive_title}</span>}
                            </div>
                            <span style={{ fontSize: 10, fontWeight: 700, color: "#fff", background: "#F59E0B", padding: "2px 8px", borderRadius: 10 }}>
                              {ccpSourceLabel(im.list_source)}
                            </span>
                          </div>
                          <div style={{ fontSize: 11, color: "#991B1B", marginTop: 4 }}>
                            Listed as: <strong>{im.listed_name}</strong>
                            {im.match_score != null && <span style={{ marginLeft: 8 }}>Score: {(im.match_score * 100).toFixed(0)}%</span>}
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              </SectionCard>
            )}

            {/* Sources not triggered */}
            {uncheckedEvidence.length > 0 && (
              <SectionCard title="Sources Checked — No Signal">
                <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
                  {uncheckedEvidence.map(ev => (
                    <span key={ev.source_key} style={{
                      fontSize: 10, fontWeight: 600, padding: "3px 10px", borderRadius: 99,
                      background: `${T.success}10`, color: T.success, border: `1px solid ${T.success}30`,
                    }}>
                      {ccpSourceLabel(ev.source_key)} — Clear
                    </span>
                  ))}
                </div>
              </SectionCard>
            )}

            {/* Hard floor enforcement banner */}
            {profile.ccp_flag && (
              <div style={{
                background: "#1E1B4B", borderRadius: T.radiusSm,
                padding: "12px 18px", display: "flex", alignItems: "center", gap: 10,
              }}>
                <div>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#C4B5FD", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                    CCP Hard Floor: Enforced in Code, Not Policy
                  </div>
                  <div style={{ fontSize: 11, color: "#A5B4FC", marginTop: 2 }}>
                    Level 2+ flags cannot be downgraded by the LLM. The audit trail shows every source, every signal, every weight.
                  </div>
                </div>
              </div>
            )}
          </div>
        );
      })()}
    </div>
  );
}
